import { Fragment } from "react";
import { dateTimeUtils, IDateTimeBox, IFormField, ITextField, IUIItem, stringUtils } from "cayo.ui";
import { SoftLink } from "@cayo/shared/api/types/cayo-graph";
import { IJsonForm, ILayoutItem } from "@cayo/shared/api/types/schema";
import auxPropsUtils, {
  fixMvValue,
  resolveProperyExpression,
} from "@cayo/shared/components/Form/modelUtils/auxPropsUtils";
import { setModelProperty } from "@cayo/shared/components/Form/modelUtils/setModelProperty";
import {
  IAdditionalFormFieldProps,
  IAdditionalFormProps,
  TGenerationRulesList,
  TPropertiesUsedInGeneration,
} from "@cayo/shared/components/Form/types";
import IAuxRenderParams from "@cayo/shared/components/schemes/types/AuxRenderProps";
import logger from "@cayo/shared/libs/logger";
import { IActionBuilder } from "@cayo/shared/scheme/actions";
import { evaluateODataLogicalExpression } from "@cayo/shared/scheme/transforms/odataLogicalExpressionEvaluator";
import { nameofFactory, objectUtils } from "@cayo/shared/utils/object";
import formRenderers, { formComponentTypes } from "./form-renderers";
import pickerRenderers, { pickerTypes } from "./picker-renderers";
import { buttonRenderers, buttonTypes } from "./renderers.buttons";
import { IObjectPicker, ITabLayoutItem } from "../../../api/schema.api";
import { parseGenerationRule } from "../../../scheme/transforms/generation-rule-parser";
import { IPickerItem } from "../../ObjectPicker/IPickerItem";
import { StringsToEntities } from "../../ObjectPicker/logic";
import {
  ILayoutHierarchy,
  layoutRenderers,
  layoutTypes,
} from "../../Schemes/renderers/renderers.layout";
import modelUtils from "../modelUtils";
import { getPropValue, isPicker, isPropertyGroup } from "../modelUtils/build-model";
import { getValueConverter } from "../modelUtils/get-value-converter";
import { deleteModelProperty } from "../modelUtils/utils";

type ComplexItem = IUIItem & {
  items?: IUIItem[];
  customView?: string;
};

const itemPropNames = nameofFactory<IFormField>();

const renderForm = (
  props: IJsonForm & { actionBuilder?: IActionBuilder },
  addProps: IAdditionalFormProps
) => {
  if (!props?.items?.length) {
    return <Fragment />;
  }

  const { actionBuilder } = props;

  const visibleItems = modelUtils.getVisibleItems(
    props.items,
    addProps.model,
    addProps?.parentModel
  );

  const visibleLayouts: ILayoutItem[] = modelUtils.getVisibleItems(
    props.layout || [],
    addProps.model,
    addProps?.parentModel
  );

  const hiddenItemNames = new Set(
    objectUtils
      // filter out items that are not visible
      .arrayDiff(props.layout || [], visibleLayouts)
      // flatten nested items
      .flatMap((hiddenLayout) => {
        if (hiddenLayout.type === "tab") {
          const tabItems = hiddenLayout.items as unknown as ITabLayoutItem[];
          return tabItems.flatMap((tab) => tab.items);
        }
        return hiddenLayout.items ?? [];
      })
  );

  if (
    visibleItems.length === 0 &&
    isPropertyGroup(props) &&
    (props as ITextField).nullValueDecorator
  ) {
    return (props as ITextField).nullValueDecorator;
  }

  let result = visibleItems!.map<ILayoutHierarchy>((a: ComplexItem, i) => ({
    render: () => {
      const { model, errors, setErrorCallback, setModel, propertyAnnotations } = addProps;

      const type = (a as ComplexItem).customView || a.type;

      const buttonRenderName = stringUtils.dashedToLowCase(a.type) as buttonTypes;
      const buttonRenderer = a.type ? buttonRenderers[buttonRenderName] : undefined;

      if (buttonRenderer) {
        // a.type === "submit-button" ? addProps.isSaveDisabled : undefined,
        return buttonRenderer(
          a as any,
          {
            theme: addProps.theme,
            actionBuilder,
            handleResponse: addProps.handleResponse,
            signals: addProps.signals,
          } as IAuxRenderParams
        );
      }

      const renderKey = stringUtils.dashedToLowCase(type);
      const formFieldRenderer =
        (type &&
          (formRenderers[renderKey as formComponentTypes] ||
            pickerRenderers[renderKey as pickerTypes])) ??
        formRenderers.text;
      if (formFieldRenderer) {
        const picker = isPicker(a) ? (a as unknown as IObjectPicker) : undefined;
        const childItems = a.items;

        const selectPropertyName = picker?.selectPropertyName;

        const name = (a as any).modelProperty || a.name;
        let value =
          selectPropertyName && !picker.valueTemplate
            ? StringsToEntities(model[name!], selectPropertyName)
            : model[name];

        if (value === undefined) {
          value = getPropValue(a, model, true);
        }

        if (isPropertyGroup(a) && a.bindings && childItems?.length) {
          childItems.forEach((childItem) => (childItem.bindings = a.bindings));
        }

        if (typeof value === "string" && value.indexOf("{") >= 0 && addProps?.parentModel) {
          const unresolvedValue = value;
          value = resolveProperyExpression(unresolvedValue, addProps?.parentModel);
          log.debug(
            `Resolve value expression, name:`,
            a.name,
            "unresolvedValue:",
            unresolvedValue,
            "model:",
            addProps?.parentModel,
            "result:",
            value
          );
        }

        const addFormFieldProps: IAdditionalFormFieldProps = {
          value,
          error: errors[name],
          disabled: (() => {
            // if (errors[a.name!]) {
            //   setErrorCallback(a.name!, undefined);
            // }

            if (a.bindings) {
              const disabledKey = Object.keys(a.bindings).find(
                (p) => p === itemPropNames("disabled")
              );
              const expression = disabledKey && a.bindings[disabledKey];
              if (expression) {
                const result = evaluateODataLogicalExpression(expression, model);

                let hasChanges = false;
                if (result) {
                  hasChanges = auxPropsUtils.setPropDisabled(model, name!);
                } else {
                  hasChanges = auxPropsUtils.setPropEnabled(model, name!);
                }

                if (hasChanges) {
                  setModel({ type: "update", model });
                }
                return result;
              }
            }
            return false;
          })(),
          setValue: (v, annotation) => {
            const name = (annotation as any).modelProperty || annotation.name;
            if (picker) {
              model![name!] =
                (v && (v as IPickerItem[])?.map<SoftLink>((ii) => ii.link as any)) || [];
            } else {
              model![name!] = v;
            }

            const original = addProps.object;
            if (original) {
              let originalValue = modelUtils.resolvePropertyValue(name, original);

              let currentValue = v;
              const converter = getValueConverter(annotation).from;
              if (converter) {
                originalValue = converter(fixMvValue(originalValue, annotation.type));
                currentValue = converter(fixMvValue(currentValue, annotation.type));
              }
              if (
                annotation.type === "datetimebox" &&
                (annotation as IDateTimeBox)?.editMode === "onlyTime"
              ) {
                // date time box
                originalValue = originalValue.slice(0, 5);
                try {
                  currentValue = dateTimeUtils.convertTimeToUtc(currentValue);
                } catch (e) {
                  log.error(e);
                }
              }

              if (!annotation.isStandalone) {
                if (objectUtils.areObjectsEqual(originalValue, currentValue)) {
                  deleteModelProperty(name, model);
                } else {
                  setModelProperty(name, true, model);
                }
              }

              const generationRulesList: TGenerationRulesList = model?.generationRulesList;
              const propertiesUsedInGeneration: TPropertiesUsedInGeneration =
                model?.propertiesUsedInGeneration;

              if (propertiesUsedInGeneration?.has(name)) {
                for (const genProp in generationRulesList) {
                  // skip value generation for the field which is being edited
                  if (genProp === name) {
                    continue;
                  }
                  const ruleObj = generationRulesList[genProp];
                  const { triggerProperties, generationRule } = ruleObj;
                  // check if triggerProp has changed
                  const hasFieldName = triggerProperties.has(name);

                  if (hasFieldName) {
                    model[genProp] = parseGenerationRule(generationRule, model);
                  }
                }
              }
            }

            setModel({ type: "update", model });
          },
          setError: setErrorCallback,
          intl: addProps.intl,
          object: addProps?.object,
          propertyAnnotations: propertyAnnotations,
          setMessage: addProps?.setMessage,
          getIconAnnotation: addProps?.getIconAnnotation,
          theme: addProps?.theme,
          signals: addProps?.signals,
          formId: addProps?.formId,
        };

        try {
          return formFieldRenderer(a, addFormFieldProps, addProps);
        } catch (e) {
          log.error(`Error rendering form field ${name} of type ${a.type}`, e);
          return formRenderers.text(a as any, addFormFieldProps);
        }
      }
    },
    index: i,
    name: a.name,
  }));

  visibleLayouts?.forEach((l) => {
    const intersection = visibleItems?.filter((f) => {
      const name = f.name!;

      if (l.type === "tab") {
        const innerItems = l.items as unknown as ITabLayoutItem[];
        return !!innerItems.find((ti) => ti.items.includes(name));
      }

      return l.items?.includes(name) || hiddenItemNames.has(name);
    });

    if (intersection?.length) {
      const index = visibleItems!.findIndex((f) => f.name! === intersection[0].name);
      const excludedIds = intersection.map((i) => i.name);

      const childrenRenderers = result.filter((f) => excludedIds.includes(f.name));

      l.name = l.name || `${props.title}_layout_${index}`;

      // TODO: find better way to determine if sizes should be applied, see #29429
      const useSizes = intersection.length > 1 && intersection[0].type === "toggle";
      const lConfig: ILayoutHierarchy = {
        render: () => layoutRenderers[l.type as layoutTypes](l as any, childrenRenderers, useSizes),
        index,
      };

      const resultIndex = result.findIndex((r) => r.index === index);
      result[resultIndex] = lConfig;

      result = result.filter((f) => !excludedIds.includes(f.name));
    }
  });

  return result.map((r) => <Fragment key={r.name ?? r.index}>{r.render()}</Fragment>);
};

const log = logger.getLogger("form.renderer");

export default renderForm;
